import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Loader } from '@/components/common/Loader';
import { Sheet } from '@/components/common/Sheet';
import { SfidaSheetDaCatalogo } from '@/components/social/SfidaSheet';
import { env } from '@/env';
import { useSfidaDiPiattaforma } from '@/hooks/useSfidaDiPiattaforma';
import { useSfideSenzaConversazione } from '@/hooks/useSfideNelDm';
import { amiciConConversazione } from '@/lib/limulo/sfideNelDm';
import { getMyLogtoSub } from '@/lib/logto';
import { conversazioniDaMostrare, percorsoConversazione } from '@/lib/qsocials/dm';
import { useStatoTempoReale } from '@/lib/qsocials/statoTempoReale';
import type { AzioneRiga, LatoSwipe } from '@/lib/qsocials/swipeRiga';
import { ricontrolloAmiciMs } from '@/lib/qsocials/tempoReale';
import { useListDmConversationsQuery, useMarkDmReadMutation } from '@/services/dmApi';
import { useBlockUserMutation } from '@/services/socialApi';
import type { DmConversation } from '@/types/dm';

import { CancellaMieiMessaggi } from './CancellaMieiMessaggi';
import { RigaConversazione } from './RigaConversazione';
import { SfideRicevuteNelDm } from './SfideRicevuteNelDm';

/**
 * L'elenco delle conversazioni, v2 (L-2238: A1 (c), A2 (a), A3 (1)): una conversazione per riga, tocco = entri,
 * swipe = azioni (`RigaConversazione`). Le chiuse (§1-ter `closed`) restano, spente e senza anteprima; le aperte
 * e mai scritte non si mostrano. In fondo, «Cancella i miei messaggi» (D4, L-2231 (a)).
 *
 * Una sola riga aperta alla volta, e la tiene questo elenco. Un tocco FUORI dalla riga aperta la richiude e
 * basta: non entra in un'altra conversazione e non comincia un altro gesto (`ignoraClick`, azzerato a OGNI
 * `pointerdown` — un flag rimasto da un gesto il cui click non e' arrivato mangerebbe il tocco dopo).
 */
export function ElencoConversazioni() {
  const { t } = useTranslation('general');
  const navigate = useNavigate();
  const ricontrollo = ricontrolloAmiciMs(env.qsocialsWsUrl, useStatoTempoReale());
  const elenco = useListDmConversationsQuery(undefined, {
    refetchOnFocus: true,
    pollingInterval: ricontrollo,
    skipPollingIfUnfocused: true,
  });
  const [segnaLetto] = useMarkDmReadMutation();
  const [blocca, blocco] = useBlockUserMutation();
  const me = getMyLogtoSub();
  // La serratura del bottone «Sfida» (e del pannello che apre): UNA lettura per l'elenco, non una per riga.
  const sfidaAccesa = useSfidaDiPiattaforma();
  const righe = conversazioniDaMostrare(elenco.data);
  // T2-5 (B3a): le sfide di piattaforma ricevute da chi NON ha una conversazione, in cima. Sotto serratura soltanto.
  const conConversazione = useMemo(() => amiciConConversazione(elenco.data), [elenco.data]);
  const sfide = useSfideSenzaConversazione(conConversazione);

  const [aperta, setAperta] = useState<{ id: string; lato: LatoSwipe } | null>(null);
  const [daBloccare, setDaBloccare] = useState<DmConversation | null>(null);
  const [daSfidare, setDaSfidare] = useState<DmConversation | null>(null);
  const [erroreBlocco, setErroreBlocco] = useState(false);
  const ignoraClick = useRef(false);
  const apertaRef = useRef(aperta);
  apertaRef.current = aperta;

  // Il tocco altrove. In cattura sul documento: arriva PRIMA dei gestori della riga, che leggono il flag.
  useEffect(() => {
    const giu = (e: PointerEvent) => {
      ignoraClick.current = false;
      const a = apertaRef.current;
      if (!a) return;
      const bersaglio = e.target instanceof Element ? e.target : null;
      // Sui bottoni della riga aperta no: quelli agiscono.
      if (bersaglio?.closest(`[data-riga-id="${CSS.escape(a.id)}"] [data-vassoio]`)) return;
      // Sulla riga aperta stessa: il gesto parte dalla posizione aperta, il tocco la richiude (click).
      if (bersaglio?.closest(`[data-riga-id="${CSS.escape(a.id)}"]`)) return;
      setAperta(null);
      ignoraClick.current = true;
    };
    document.addEventListener('pointerdown', giu, true);
    return () => document.removeEventListener('pointerdown', giu, true);
  }, []);

  const onApri = useCallback((id: string, lato: LatoSwipe | null) => {
    setAperta(lato === null ? null : { id, lato });
  }, []);

  const onEntra = useCallback((c: DmConversation) => navigate(percorsoConversazione(c.id)), [navigate]);

  const onAzione = useCallback(
    (c: DmConversation, azione: AzioneRiga) => {
      if (azione === 'letto') void segnaLetto(c.id);
      else if (azione === 'profilo') navigate(`/profile/${encodeURIComponent(c.friendUserId)}`);
      else if (azione === 'blocca') {
        // Mai senza conferma: anche lo swipe lungo apre soltanto il foglio.
        setErroreBlocco(false);
        setDaBloccare(c);
      } else if (azione === 'sfida') {
        // Nessuna scrittura dal gesto: anche lo swipe lungo apre soltanto il pannello di Amici, e la sfida
        // nasce dalla conferma dentro di lui.
        setDaSfidare(c);
      }
    },
    [navigate, segnaLetto],
  );

  const confermaBlocco = async () => {
    if (!daBloccare) return;
    try {
      // Il blocco invalida anche `DmConversations` e `DmUnread` (socialApi): la riga torna `closed`.
      await blocca({ friendUserId: daBloccare.friendUserId }).unwrap();
      setDaBloccare(null);
    } catch {
      setErroreBlocco(true);
    }
  };

  // La regola di Amici (`FriendListItem`, `||`): mai il `sub` grezzo, e nemmeno una stringa vuota.
  const nomeDaSfidare = daSfidare
    ? (daSfidare.displayName || t('social.unknown_user', { defaultValue: 'QLASH player' }))
    : '';
  const nomeDaBloccare = daBloccare ? (daBloccare.displayName ?? daBloccare.friendUserId) : '';

  return (
    <div className="flex flex-col gap-4">
      {elenco.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      ) : righe.length === 0 && !sfide.ce ? (
        <p className="rounded-2xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">{t('social.dm.elencoVuoto')}</p>
      ) : (
        // A tutta larghezza: la pagina ha `px-4`, la lista ci esce sopra (come nel prototipo v2).
        <ul className="-mx-4 flex flex-col divide-y divide-border" data-testid="dm-elenco">
          <SfideRicevuteNelDm sfide={sfide} come="riga" />
          {righe.map((c) => (
            <RigaConversazione
              key={c.id}
              c={c}
              me={me}
              sfida={sfidaAccesa}
              aperta={aperta?.id === c.id ? aperta.lato : null}
              ignoraClick={ignoraClick}
              onApri={onApri}
              onAzione={onAzione}
              onEntra={onEntra}
            />
          ))}
        </ul>
      )}
      <CancellaMieiMessaggi />
      {/* Montato SOLO mentre e' aperto: legge `/v1/games` e prepara l'invio, e l'elenco non deve pagarli prima. */}
      {daSfidare && (
        <SfidaSheetDaCatalogo avversario={nomeDaSfidare} amicoId={daSfidare.friendUserId} onClose={() => setDaSfidare(null)} />
      )}
      <Sheet open={daBloccare !== null} onClose={() => setDaBloccare(null)} label={t('social.dm.blocca.titolo', { name: nomeDaBloccare })}>
        <div className="flex flex-col gap-3 px-5 pb-6 pt-2">
          <p className="text-lg font-extrabold text-fg">{t('social.dm.blocca.titolo', { name: nomeDaBloccare })}</p>
          <p className="text-sm text-fg">{t('social.dm.blocca.testo', { name: nomeDaBloccare })}</p>
          {erroreBlocco ? (
            <p role="alert" className="text-sm text-danger">
              {t('social.dm.blocca.errore')}
            </p>
          ) : null}
          <button
            type="button"
            data-testid="dm-blocca-conferma"
            disabled={blocco.isLoading}
            onClick={() => void confermaBlocco()}
            className="mt-2 h-12 rounded-2xl bg-danger font-extrabold text-white disabled:opacity-50"
          >
            {t('social.dm.blocca.conferma')}
          </button>
          <button
            type="button"
            onClick={() => setDaBloccare(null)}
            className="h-12 rounded-2xl bg-bg-secondary font-bold text-fg"
          >
            {t('social.dm.blocca.annulla')}
          </button>
        </div>
      </Sheet>
    </div>
  );
}
